import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { LogoUpload } from '@/components/LogoUpload';
import { PartnerModalFormData, partnerModalFormSchema } from './schemas';
import { useCreatePartnerMutation } from '@/services/queries/partner';
import { CreatePartnerPayload } from '@/services/request/partner';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

export interface PartnerCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const PartnerCreateModal = ({ isOpen, onClose, onDone }: PartnerCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const createPartnerMutation = useCreatePartnerMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<PartnerModalFormData>({
    defaultValues: { name: '', file: undefined },
    resolver: zodResolver(partnerModalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: PartnerModalFormData) => {
      if (!data.name || !data.file) {
        return;
      }

      const payload: CreatePartnerPayload = {
        logo: data.file,
        name: data.name,
      };

      createPartnerMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.partners.partners_section.components.partner_create_modal.partner_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.partners.partners_section.components.partner_create_modal.partner_create_error'),
          });
        },
      });
    },
    [createPartnerMutation, onDone, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('pages.partners.partners_section.components.partner_create_modal.partner_create_title')}</ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.partners.partners_section.components.partner_create_modal.partner_create_name_label')}
              {...register('name')}
              helperText={t('pages.partners.partners_section.components.partner_create_modal.partner_create_name_helper')}
              maxLength={30}
              rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <Text fontWeight='bold' mb={2}>
                {t('pages.partners.partners_section.components.partner_create_modal.partner_create_image_label')}
              </Text>

              <LogoUpload imageURL={''} onFileChange={file => setValue('file', file)} error={errors?.file?.message?.toString()} />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.partners.partners_section.components.partner_create_modal.partner_create_cancel_button')}
            </Button>
            <Button onClick={handleConfirm}>{t('pages.partners.partners_section.components.partner_create_modal.partner_create_save_button')}</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={createPartnerMutation.isLoading} />
    </Modal>
  );
};
